import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Switch, FormControlLabel,
  Card, CardContent, TextField, Button, Divider,
  List, ListItem, ListItemText, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert,
  Snackbar, CircularProgress, Tooltip
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Send as SendIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { EmailMessage } from '../services/emailService';
import { generateEmailResponse } from '../services/emailAIService';

// Interface para regra de resposta automática
interface AutoResponseRule {
  id: string;
  name: string;
  active: boolean;
  conditions: {
    fromContains?: string[];
    subjectContains?: string[];
    bodyContains?: string[];
    leadCategory?: string[];
  };
  responseTemplate: string;
  useAI: boolean;
  aiPrompt?: string;
  sendDelay: number; // em minutos
  lastRun?: Date;
}

// Interface para histórico de envio automático
interface AutoSendHistory {
  id: string;
  emailId: string;
  recipient: string;
  subject: string;
  sentAt: Date;
  status: 'success' | 'failed';
  ruleId: string;
}

const EmailAutoResponder: React.FC = () => {
  // Estados
  const [autoResponderEnabled, setAutoResponderEnabled] = useState(false);
  const [rules, setRules] = useState<AutoResponseRule[]>([]);
  const [sendHistory, setSendHistory] = useState<AutoSendHistory[]>([]);
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoResponseRule | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [loading, setLoading] = useState(false);
  
  // Obter leads do Redux store
  const leads = useSelector((state: RootState) => state.leads.items);
  
  // Carregar regras e histórico (simulado)
  useEffect(() => {
    // Simular carregamento de regras do servidor
    const mockRules: AutoResponseRule[] = [
      {
        id: 'rule-1',
        name: 'Responder consultas de preço',
        active: true,
        conditions: {
          subjectContains: ['preço', 'valor', 'custo'],
          bodyContains: ['quanto custa', 'qual o valor', 'preço']
        },
        responseTemplate: 'Prezado(a) {nome},\n\nObrigado pelo seu interesse em nossos imóveis. Temos diversas opções de preços dependendo da localização e características.\n\nPoderia nos informar qual região é do seu interesse para que possamos fornecer informações mais precisas?\n\nAtenciosamente,\nEquipe de Vendas',
        useAI: true,
        aiPrompt: 'Gere uma resposta profissional para uma consulta sobre preços de imóveis. Peça mais informações sobre a região de interesse e orçamento do cliente.',
        sendDelay: 15
      },
      {
        id: 'rule-2',
        name: 'Agendar visitas',
        active: true,
        conditions: {
          subjectContains: ['visita', 'conhecer', 'ver imóvel'],
          bodyContains: ['agendar', 'visitar', 'conhecer']
        },
        responseTemplate: 'Prezado(a) {nome},\n\nFicamos felizes com seu interesse em visitar nossos imóveis. Temos disponibilidade para agendamentos de segunda a sábado, das 9h às 18h.\n\nQual seria o melhor dia e horário para você?\n\nAtenciosamente,\nEquipe de Vendas',
        useAI: true,
        aiPrompt: 'Gere uma resposta para agendar uma visita a um imóvel. Ofereça opções de horários e peça confirmação de data.',
        sendDelay: 10
      }
    ];
    
    setRules(mockRules);
    
    // Simular histórico de envios
    const mockHistory: AutoSendHistory[] = [
      {
        id: 'send-1',
        emailId: 'email-123',
        recipient: 'cliente@exemplo.com',
        subject: 'RE: Preço do apartamento',
        sentAt: new Date(2025, 3, 5, 14, 30),
        status: 'success',
        ruleId: 'rule-1'
      },
      {
        id: 'send-2',
        emailId: 'email-124',
        recipient: 'interessado@exemplo.com',
        subject: 'RE: Visita ao imóvel',
        sentAt: new Date(2025, 3, 5, 16, 15),
        status: 'success',
        ruleId: 'rule-2'
      },
      {
        id: 'send-3',
        emailId: 'email-125',
        recipient: 'potencial@exemplo.com',
        subject: 'RE: Valor do imóvel',
        sentAt: new Date(2025, 3, 6, 9, 45),
        status: 'failed',
        ruleId: 'rule-1'
      }
    ];
    
    setSendHistory(mockHistory);
  }, []);
  
  // Função para abrir o diálogo de edição de regra
  const handleEditRule = (rule: AutoResponseRule | null) => {
    setEditingRule(rule || {
      id: `rule-${Date.now()}`,
      name: '',
      active: true,
      conditions: {},
      responseTemplate: '',
      useAI: true,
      sendDelay: 15
    } as AutoResponseRule);
    setOpenRuleDialog(true);
  };
  
  // Função para salvar regra
  const handleSaveRule = () => {
    if (!editingRule || !editingRule.name) return;
    
    if (editingRule.id && rules.some(r => r.id === editingRule.id)) {
      // Atualizar regra existente
      setRules(prev => prev.map(r => r.id === editingRule.id ? editingRule : r));
      setSnackbarMessage(`Regra "${editingRule.name}" atualizada com sucesso.`);
    } else {
      // Adicionar nova regra
      setRules(prev => [...prev, editingRule]);
      setSnackbarMessage(`Regra "${editingRule.name}" criada com sucesso.`);
    }
    
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    setOpenRuleDialog(false);
    setEditingRule(null);
  };
  
  // Função para excluir regra
  const handleDeleteRule = (ruleId: string) => {
    setRules(prev => prev.filter(r => r.id !== ruleId));
    setSnackbarMessage('Regra excluída com sucesso.');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };
  
  // Função para alternar status da regra
  const handleToggleRule = (ruleId: string) => {
    setRules(prev => prev.map(r => 
      r.id === ruleId ? { ...r, active: !r.active } : r
    ));
  };
  
  // Função para testar regra
  const handleTestRule = async (rule: AutoResponseRule) => {
    setLoading(true);
    try {
      // Criar e-mail de teste
      const testEmail: EmailMessage = {
        id: `test-${Date.now()}`,
        from: 'teste@exemplo.com',
        to: 'contato@imobiliaria.com',
        subject: 'Teste de regra automática',
        body: 'Este é um e-mail de teste para a regra automática.',
        date: new Date(),
        read: false,
        replied: false,
        status: 'received'
      };
      
      // Gerar resposta com base na regra
      let responseBody = rule.responseTemplate;
      
      if (rule.useAI) {
        try {
          const aiResponse = await generateEmailResponse(testEmail, null, {
            temperature: 0.7,
            personality: 'profissional e prestativo'
          });
          
          responseBody = aiResponse.body;
        } catch (error) {
          console.error('Erro ao gerar resposta com IA:', error);
        }
      }
      
      // Simular envio
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Adicionar ao histórico
      const newSend: AutoSendHistory = {
        id: `send-test-${Date.now()}`,
        emailId: testEmail.id,
        recipient: 'teste@exemplo.com',
        subject: `RE: ${testEmail.subject}`,
        sentAt: new Date(),
        status: 'success',
        ruleId: rule.id
      };
      
      setSendHistory(prev => [newSend, ...prev]);
      
      setSnackbarMessage('Teste de regra realizado com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erro ao testar regra:', error);
      setSnackbarMessage('Erro ao testar regra. Tente novamente.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para iniciar/parar o responder automático
  const handleToggleAutoResponder = () => {
    setAutoResponderEnabled(prev => !prev);
    
    setSnackbarMessage(autoResponderEnabled 
      ? 'Resposta automática desativada.' 
      : 'Resposta automática ativada! O sistema responderá automaticamente aos e-mails conforme as regras configuradas.');
    setSnackbarSeverity(autoResponderEnabled ? 'info' : 'success');
    setSnackbarOpen(true);
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <AutoAwesomeIcon sx={{ mr: 1 }} />
        Gerador de Respostas e Enviador Automático
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Configure regras para responder automaticamente a e-mails e agende envios com base em critérios específicos.
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Resposta Automática
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={autoResponderEnabled}
                onChange={handleToggleAutoResponder}
                color={autoResponderEnabled ? 'success' : 'default'}
              />
            }
            label={autoResponderEnabled ? 'Ativado' : 'Desativado'}
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleEditRule(null)}
          >
            Nova Regra
          </Button>
          
          <Button
            variant="outlined"
            startIcon={showHistory ? <SettingsIcon /> : <ScheduleIcon />}
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? 'Mostrar Regras' : 'Mostrar Histórico'}
          </Button>
        </Box>
        
        {!showHistory ? (
          <List>
            {rules.length > 0 ? (
              rules.map((rule) => (
                <Card key={rule.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {rule.name}
                      </Typography>
                      
                      <Box>
                        <Tooltip title="Testar regra">
                          <IconButton 
                            onClick={() => handleTestRule(rule)}
                            disabled={loading}
                            color="primary"
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Editar regra">
                          <IconButton 
                            onClick={() => handleEditRule(rule)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Excluir regra">
                          <IconButton 
                            onClick={() => handleDeleteRule(rule.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Switch
                          checked={rule.active}
                          onChange={() => handleToggleRule(rule.id)}
                          size="small"
                        />
                      </Box>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Condições:</strong>
                        </Typography>
                        
                        {rule.conditions.subjectContains && rule.conditions.subjectContains.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Assunto contém:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {rule.conditions.subjectContains.map((term, index) => (
                                <Chip 
                                  key={index} 
                                  label={term} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                        
                        {rule.conditions.bodyContains && rule.conditions.bodyContains.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Corpo contém:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {rule.conditions.bodyContains.map((term, index) => (
                                <Chip 
                                  key={index} 
                                  label={term} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Configurações:</strong>
                        </Typography>
                        
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            icon={<ScheduleIcon />} 
                            label={`Enviar após ${rule.sendDelay} minutos`} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                            sx={{ mr: 1, mb: 1 }}
                          />
                          
                          <Chip 
                            icon={<AutoAwesomeIcon />} 
                            label={rule.useAI ? 'Usar IA' : 'Resposta fixa'} 
                            size="small" 
                            color={rule.useAI ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        </Box>
                        
                        {rule.lastRun && (
                          <Typography variant="caption" color="text.secondary">
                            Última execução: {rule.lastRun.toLocaleString()}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Nenhuma regra configurada. Clique em "Nova Regra" para começar.
                </Typography>
              </Box>
            )}
          </List>
        ) : (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Histórico de Envios Automáticos
            </Typography>
            
            {sendHistory.length > 0 ? (
              <List>
                {sendHistory.map((item) => (
                  <ListItem key={item.id} divider>
                    <ListItemText
                      primary={item.subject}
                      secondary={
                        <>
                          <Typography variant="body2" component="span">
                            Para: {item.recipient}
                          </Typography>
                          <br />
                          <Typography variant="caption" component="span">
                            {item.sentAt.toLocaleString()} - 
                            {item.status === 'success' ? (
                              <Chip 
                                label="Enviado" 
                                size="small" 
                                color="success" 
                                sx={{ ml: 1 }} 
                              />
                            ) : (
                              <Chip 
                                label="Falha" 
                                size="small" 
                                color="error" 
                                sx={{ ml: 1 }} 
                              />
                            )}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  Nenhum envio automático registrado.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Diálogo para editar/criar regra */}
      <Dialog 
        open={openRuleDialog} 
        onClose={() => setOpenRuleDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingRule && editingRule.id && rules.some(r => r.id === editingRule.id) 
            ? `Editar Regra: ${editingRule.name}` 
            : 'Nova Regra de Resposta Automática'}
        </DialogTitle>
        
        <DialogContent dividers>
          {editingRule && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="Nome da Regra"
                  fullWidth
                  value={editingRule.name}
                  onChange={(e) => setEditingRule({...editingRule, name: e.target.value})}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Condições de Ativação
                </Typography>
                
                <TextField
                  label="Assunto contém (separados por vírgula)"
                  fullWidth
                  value={editingRule.conditions.subjectContains?.join(', ') || ''}
                  onChange={(e) => setEditingRule({
                    ...editingRule, 
                    conditions: {
                      ...editingRule.conditions,
                      subjectContains: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    }
                  })}
                  margin="normal"
                />
                
                <TextField
                  label="Corpo contém (separados por vírgula)"
                  fullWidth
                  value={editingRule.conditions.bodyContains?.join(', ') || ''}
                  onChange={(e) => setEditingRule({
                    ...editingRule, 
                    conditions: {
                      ...editingRule.conditions,
                      bodyContains: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    }
                  })}
                  margin="normal"
                />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Atraso no Envio</InputLabel>
                  <Select
                    value={editingRule.sendDelay}
                    onChange={(e) => setEditingRule({
                      ...editingRule,
                      sendDelay: e.target.value as number
                    })}
                    label="Atraso no Envio"
                  >
                    <MenuItem value={0}>Imediato</MenuItem>
                    <MenuItem value={5}>5 minutos</MenuItem>
                    <MenuItem value={10}>10 minutos</MenuItem>
                    <MenuItem value={15}>15 minutos</MenuItem>
                    <MenuItem value={30}>30 minutos</MenuItem>
                    <MenuItem value={60}>1 hora</MenuItem>
                    <MenuItem value={120}>2 horas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Configurações de Resposta
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingRule.useAI}
                      onChange={(e) => setEditingRule({
                        ...editingRule,
                        useAI: e.target.checked
                      })}
                    />
                  }
                  label="Usar IA para gerar resposta"
                  sx={{ mb: 2 }}
                />
                
                {editingRule.useAI ? (
                  <TextField
                    label="Prompt para IA"
                    fullWidth
                    multiline
                    rows={4}
                    value={editingRule.aiPrompt || ''}
                    onChange={(e) => setEditingRule({
                      ...editingRule,
                      aiPrompt: e.target.value
                    })}
                    helperText="Descreva como a IA deve responder a este tipo de e-mail"
                  />
                ) : (
                  <TextField
                    label="Modelo de Resposta"
                    fullWidth
                    multiline
                    rows={8}
                    value={editingRule.responseTemplate}
                    onChange={(e) => setEditingRule({
                      ...editingRule,
                      responseTemplate: e.target.value
                    })}
                    helperText="Use {nome} para inserir o nome do destinatário"
                  />
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenRuleDialog(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveRule} 
            variant="contained" 
            disabled={!editingRule || !editingRule.name}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailAutoResponder;
